import { Button, Card, Input, Modal, Space, Tag } from 'antd';
import React, { useEffect, useMemo, useState } from 'react';

import type { TechStackItem } from '@/api/project-details/mocks/projectHome.types';
import Text from '@/atoms/Text';
import { Pill } from '@/components/common/Pill';
import { PREDEFINED_TECH_STACK, TECH_CATEGORIES } from '@/data/predefinedTechStack';
import { CloseIcon as CloseOutlined } from '@/icons';
import { createLogger } from '@/utils/logging';

import './TechStackSelector.scss';

const logger = createLogger('TechStackSelector', 'component');

export interface TechStackSelectorProps {
  items: TechStackItem[];

  /**
   * Called when a new technology is selected.
   *
   * The parent calls the POST API and receives the real database ID.
   */
  onItemAdd?: (item: { name: string; category: string }) => Promise<void>;

  /**
   * Called with the REAL database ID of the item.
   *
   * The parent calls the DELETE API.
   */
  onItemDelete?: (itemId: string) => Promise<void>;

  isLoading?: boolean;
  isDeletingId?: string | null;

  /**
   * Whether the current user can manage tech stack (add/remove).
   * If false, component is read-only.
   */
  canManageTechStack?: boolean;
}

export function TechStackSelector({
  items,
  onItemAdd,
  onItemDelete,
  isLoading = false,
  isDeletingId = null,
  canManageTechStack = false,
}: Readonly<TechStackSelectorProps>): React.ReactElement {
  const [selectorOpen, setSelectorOpen] = useState(false);

  const [customTechName, setCustomTechName] = useState('');

  const [selectedCategory, setSelectedCategory] = useState<string>('');

  /**
   * Track techs selected IN THE MODAL (not yet added to card).
   * Key: "name|category"
   */
  const [modalSelectedTechs, setModalSelectedTechs] = useState<Set<string>>(new Set());

  const [deletingId, setDeletingId] = useState<string | null>(isDeletingId);

  /**
   * Keep the local deleting state synchronized with the parent.
   */
  useEffect(() => {
    setDeletingId(isDeletingId);
  }, [isDeletingId]);

  /**
   * Build a set of currently selected technologies.
   *
   * We use name + category for selection state,
   * NOT the database ID.
   */
  const selectedTechNames = useMemo(
    () => new Set(items.map((item) => `${item.name}|${item.category}`)),
    [items],
  );

  /**
   * TOGGLE TECH SELECTION IN MODAL
   *
   * Toggle modal selection on/off. Doesn't add to card yet.
   * Only added when user clicks "Done".
   */
  const handleToggleTechInModal = (name: string, category: string): void => {
    const key = `${name}|${category}`;
    const newSelected = new Set(modalSelectedTechs);

    if (newSelected.has(key)) {
      newSelected.delete(key);
    } else {
      newSelected.add(key);
    }

    setModalSelectedTechs(newSelected);
  };

  /**
   * ADD ALL SELECTED TECHS TO CARD
   *
   * Called when user clicks "Done".
   * Adds all techs currently selected in the modal to the tech stack card.
   */
  const handleAddSelectedTechs = async (): Promise<void> => {
    if (!onItemAdd || modalSelectedTechs.size === 0) {
      setSelectorOpen(false);
      return;
    }

    try {
      for (const key of modalSelectedTechs) {
        const [name, category] = key.split('|');

        /**
         * Only add if not already in card.
         */
        const alreadyExists = items.some(
          (item) => item.name === name && item.category === category,
        );

        if (!alreadyExists) {
          await onItemAdd({ name, category });
        }
      }

      /**
       * Clear modal selections and close modal.
       */
      setModalSelectedTechs(new Set());
      setSelectorOpen(false);
    } catch (error) {
      logger.error('Failed to add technologies:', error);
    }
  };

  /**
   * ADD CUSTOM TECHNOLOGY
   */
  const handleAddCustomTech = async (): Promise<void> => {
    const name = customTechName.trim();
    const category = selectedCategory.trim();

    if (!name || !category || !onItemAdd) {
      return;
    }

    /**
     * Prevent duplicate technology.
     */
    const isDuplicate = items.some(
      (item) => item.name.trim().toLowerCase() === name.toLowerCase() && item.category === category,
    );

    if (isDuplicate) {
      return;
    }

    try {
      /**
       * Parent calls POST API.
       *
       * Backend returns:
       *
       * {
       *   id: "REAL-DB-ID",
       *   name: "...",
       *   category: "..."
       * }
       *
       * Parent stores that real item.
       */
      await onItemAdd({
        name,
        category,
      });

      /**
       * Clear form only after successful API call.
       */
      setCustomTechName('');
      setSelectedCategory('');
    } catch (error) {
      logger.error('Failed to add custom technology:', error);
    }
  };

  /**
   * DELETE TECHNOLOGY
   *
   * This function sends the REAL database ID exactly once.
   *
   * After the parent DELETE API succeeds, the parent updates
   * projectDetails and this component receives the new `items`.
   */
  const handleRemoveTech = async (itemId: string): Promise<void> => {
    if (!itemId || !onItemDelete) {
      return;
    }

    setDeletingId(itemId);

    try {
      /**
       * IMPORTANT:
       *
       * Only ONE DELETE API call.
       */
      await onItemDelete(itemId);
    } catch (error) {
      logger.error('Failed to delete technology:', error);
    } finally {
      setDeletingId(null);
    }
  };

  /**
   * Delete button handler.
   */
  const handleDeleteClick = (event: React.MouseEvent, itemId: string): void => {
    /**
     * Prevent the click from bubbling to any parent
     * selection handlers.
     */
    event.stopPropagation();

    void handleRemoveTech(itemId);
  };

  return (
    <>
      <Card className="tech-stack-card">
        <div className="tech-stack-card__header-top">
          <Pill tone="neutral">Source: Manual</Pill>
        </div>

        <div className="card__header">
          <Text variant="h5">Tech Stack</Text>

          <Button
            type="text"
            size="small"
            className="tech-stack-card__add-button"
            onClick={() => {
              setModalSelectedTechs(new Set());
              setSelectorOpen(true);
            }}
            disabled={isLoading || !canManageTechStack}
            loading={isLoading}
            title={!canManageTechStack ? 'You do not have permission to manage tech stack' : ''}
          >
            + Add
          </Button>
        </div>

        <div className="tech-stack-card__items">
          {items.length === 0 ? (
            <Text variant="bodySmall" color="var(--text-secondary)">
              No technologies added yet. Click &quot;Add&quot; to add Technologies.
            </Text>
          ) : (
            items.map((item) => {
              const isDeleting = deletingId === item.id;

              return (
                <Pill
                  key={item.id}
                  tone="accent"
                  className="tech-stack-card__item-tag"
                  trailing={
                    canManageTechStack ? (
                      <Button
                        type="text"
                        size="small"
                        icon={<CloseOutlined />}
                        className="tech-stack-card__remove-button"
                        onClick={(event) => handleDeleteClick(event, item.id)}
                        disabled={isDeleting}
                        loading={isDeleting}
                        title="Remove technology"
                      />
                    ) : undefined
                  }
                >
                  <span className="tech-stack-card__item-name">{item.name}</span>
                  <span className="tech-stack-card__item-category">{item.category}</span>
                </Pill>
              );
            })
          )}
        </div>
      </Card>

      <Modal
        title="Select Technologies"
        open={selectorOpen}
        onCancel={() => setSelectorOpen(false)}
        width={900}
        footer={[
          <Button key="close" onClick={() => void handleAddSelectedTechs()}>
            Done
          </Button>,
        ]}
        className="tech-stack-selector-modal"
      >
        <Space
          direction="vertical"
          className="tech-stack-selector-modal__content"
          style={{ width: '100%' }}
        >
          {/* Predefined Technologies by Category */}

          {TECH_CATEGORIES.map((category) => (
            <div key={category} className="tech-stack-selector-modal__category">
              <Text variant="bodyDefaultStrong" color="var(--text-primary)">
                {category}
              </Text>

              <Space className="tech-stack-selector-modal__tags" wrap>
                {PREDEFINED_TECH_STACK[category].map((tech) => {
                  const key = `${tech.name}|${tech.category}`;
                  const isSelected = modalSelectedTechs.has(key);
                  const isAlreadyInCard = selectedTechNames.has(key);

                  return (
                    <Tag
                      key={`${tech.name}-${tech.category}`}
                      className={`tech-stack-selector-modal__tag ${
                        isSelected ? 'tech-stack-selector-modal__tag--selected' : ''
                      }`}
                      onClick={() => {
                        if (!isLoading) {
                          handleToggleTechInModal(tech.name, tech.category);
                        }
                      }}
                      title={isAlreadyInCard ? 'Already in tech stack' : ''}
                      style={{
                        cursor: isLoading ? 'not-allowed' : 'pointer',
                        userSelect: 'none',
                        backgroundColor: isSelected
                          ? 'var(--color-brand-500)'
                          : isAlreadyInCard
                            ? 'var(--color-primary-bg)'
                            : 'var(--tag-default-bg)',
                        color: isSelected ? 'var(--text-inverse)' : 'var(--tag-default-text)',
                        borderColor: isSelected
                          ? 'var(--color-brand-500)'
                          : isAlreadyInCard
                            ? 'var(--color-brand-500)'
                            : 'var(--tag-default-border)',
                        borderStyle: 'solid',
                        borderWidth: '1px',
                        padding: '6px 12px',
                        fontSize: '13px',
                        opacity: isLoading ? 0.6 : 1,
                        display: 'inline-block',
                      }}
                    >
                      {tech.name}
                    </Tag>
                  );
                })}
              </Space>
            </div>
          ))}

          {/* Custom Technology Input */}

          <div className="tech-stack-selector-modal__custom">
            <Text variant="bodyDefaultStrong" color="var(--text-primary)">
              Add Custom Technology
            </Text>

            <Space
              direction="horizontal"
              style={{
                width: '100%',
                marginTop: '12px',
              }}
            >
              <Input
                placeholder="Technology name (e.g., Cassandra)"
                value={customTechName}
                onChange={(event) => setCustomTechName(event.target.value)}
                onPressEnter={() => {
                  void handleAddCustomTech();
                }}
                style={{ flex: 1 }}
                disabled={isLoading}
              />

              <select
                value={selectedCategory}
                onChange={(event) => setSelectedCategory(event.target.value)}
                disabled={isLoading}
                style={{
                  padding: '6px 12px',
                  borderRadius: '4px',
                  border: '1px solid var(--border-default)',
                  backgroundColor: 'white',
                  fontSize: '13px',
                }}
              >
                <option value="">Select category</option>

                {TECH_CATEGORIES.map((category) => (
                  <option key={category} value={category}>
                    {category}
                  </option>
                ))}
              </select>

              <Button
                type="primary"
                onClick={() => {
                  void handleAddCustomTech();
                }}
                disabled={!customTechName.trim() || !selectedCategory || isLoading}
                loading={isLoading}
              >
                Add
              </Button>
            </Space>
          </div>
        </Space>
      </Modal>
    </>
  );
}
